import { useCallback, useEffect, useRef, useState } from 'react';

export default function useReCaptcha(options) {
  const siteKey = options?.siteKey ?? process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const badge = options?.badge ?? 'bottomright';
  const hl = options?.hl;

  const recaptchaRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [ready, setReady] = useState(false);

  const currentResolveRef = useRef();
  const currentRejectRef = useRef();

  const onSuccess = useCallback((token) => {
    if (currentResolveRef.current) {
      currentResolveRef.current(token);
    }
  }, []);

  const onExpired = useCallback(() => {
    if (currentRejectRef.current) {
      currentRejectRef.current(new Error('reCAPTCHA token expired'));
    }
  }, []);

  const onError = useCallback(() => {
    if (currentRejectRef.current) {
      currentRejectRef.current(new Error('reCAPTCHA error'));
    }
  }, []);

  const renderWidget = useCallback(() => {
    if (!window.grecaptcha || !recaptchaRef.current || widgetIdRef.current !== null) return;

    widgetIdRef.current = window.grecaptcha.render(recaptchaRef.current, {
      sitekey: siteKey,
      size: 'invisible',
      badge,
      callback: onSuccess,
      'error-callback': onError,
      'expired-callback': onExpired,
    });
  }, [badge, onError, onExpired, onSuccess, siteKey]);

  const ensureReady = useCallback(() => {
    if (window.grecaptcha?.ready) {
      window.grecaptcha.ready(() => {
        renderWidget();
        setReady(true);
      });
    }
  }, [renderWidget]);

  const execute = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!window.grecaptcha || widgetIdRef.current === null) {
        return reject(new Error('reCAPTCHA not ready'));
      }
      currentResolveRef.current = resolve;
      currentRejectRef.current = reject;

      try {
        window.grecaptcha.execute(widgetIdRef.current);
      } catch (err) {
        reject(err);
      }
    });
  }, []);

  const reset = useCallback(() => {
    if (window.grecaptcha && widgetIdRef.current !== null) {
      window.grecaptcha.reset(widgetIdRef.current);
    }
  }, []);

  useEffect(() => {
    const existing = document.querySelector(
      'script[src^="https://www.google.com/recaptcha/api.js"]'
    );
    if (existing) {
      ensureReady();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js${hl ? `?hl=${encodeURIComponent(hl)}` : ''}`;
    script.async = true;
    script.defer = true;
    script.onload = ensureReady;
    document.head.appendChild(script);

  }, [ensureReady, hl]);

  return {
    recaptchaRef,
    ready,       
    execute,     
    reset,        
  };
}